import IconButton from '@material-ui/core/IconButton';
import {Create, ImportContacts} from '@material-ui/icons';
import {useGetToken} from 'components/hooks';
import IntegrityRoleGrid from 'components/UserRole';
import {Column} from 'devextreme-react/data-grid';
import {TABLETCOURSE} from 'features/capacitation/component/actions';
import {i18n2} from 'features/i18n/i18n';
import React from 'react';
import {useDispatch} from 'react-redux';
import {Link} from 'react-router-dom';
import SignatureDialog from '../../../components/Editors/SignatureDialog';
import {signCourse} from '../../capacitation/hookos/hooks';
import {useLoadCollaborator} from '../hooks/index';
import {useIsTablet} from 'components/hooks/useIsTablet';
import {
  useActionButtonStyles,
  getActionColumnWidth,
} from 'components/grid-dx/styles/actionButtonStyles';

interface TabletCourseGridProps {
  iD_Instance: number;
  iD_Course: number;
  status: number;
}

interface ChoiceButtonProps extends TabletCourseGridProps {
  row: any;
  approved: boolean;
}

interface SignatureButtonProps extends TabletCourseGridProps {
  row: any;
  approved: boolean;
  refresh: any;
}

interface ButtonsProps extends TabletCourseGridProps {
  data: any;
  refresh: any;
}

const SignatureButton = ({
  row,
  status,
  approved,
  refresh,
  iD_Course,
  iD_Instance,
}: SignatureButtonProps) => {
  const [signatureInfo, setSignatureInfo] = React.useState({
    name: '',
    cwid: '',
  });
  const [signatureResponse, setSignatureResponse] = React.useState({
    open: false,
    message: '',
  });
  const [open, setOpen] = React.useState(false);
  const token = useGetToken();
  const styles = useActionButtonStyles();
  const isTablet = useIsTablet();

  const handleClickOpen = () => {
    setSignatureInfo({name: row.name, cwid: row.cwid});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const submit = (
    values: {user: string; pass: string},
    formikFunctions: {resetForm: () => void},
  ) => {
    signCourse(values.user, values.pass, iD_Course, iD_Instance, token).then(
      (response) => {
        if (response.ok) {
          setOpen(false);
          refresh();
        } else {
          response.text().then((respMessage) => {
            setSignatureResponse({open: true, message: respMessage});
            formikFunctions.resetForm();
          });
        }
      },
    );
  };

  const present: boolean = row.present;

  return (
    <>
      {!approved &&
      (status === 0 || status === 3 || typeof status === 'undefined') &&
      !present ? (
        <IconButton
          className={isTablet ? styles.tabletButton : styles.compactButton}
          onClick={handleClickOpen}>
          <Create
            className={isTablet ? styles.tabletIcon : styles.compactButton}
          />
        </IconButton>
      ) : null}
      {open && (
        <SignatureDialog
          disableEscape={false}
          openDialog={open}
          onCloseDialog={handleClose}
          dialogTitle={i18n2._('Firmando ') + signatureInfo.name}
          user={signatureInfo.cwid}
          submit={(
            values: {user: string; pass: string},
            formikFunctions: {resetForm: () => void},
          ) => submit(values, formikFunctions)}
          snackbarState={signatureResponse}
          onCloseSnackbar={() => {
            setSignatureResponse({open: false, message: ''});
          }}
        />
      )}
    </>
  );
};

const ChoiceButton = ({
  row,
  status,
  approved,
  iD_Course,
  iD_Instance,
}: ChoiceButtonProps) => {
  const styles = useActionButtonStyles();
  const isTablet = useIsTablet();
  return (
    <>
      {!approved &&
        (status === 0 || status === 3 || typeof status === 'undefined') && (
          <IconButton
            className={isTablet ? styles.tabletButton : styles.compactButton}
            component={(props) => (
              <Link
                to={{
                  pathname: '/Capacitation',
                  state: {
                    iD_Course: iD_Course,
                    iD_Instance: iD_Instance,
                    modality: 1,
                    cwid: row.cwid,
                    tablet: true,
                  },
                }}
                {...props}
              />
            )}
            title={i18n2._('Tomar curso')}>
            <ImportContacts
              className={isTablet ? styles.tabletIcon : styles.compactButton}
            />
          </IconButton>
        )}
    </>
  );
};

const Buttons = ({
  data,
  status,
  refresh,
  iD_Course,
  iD_Instance,
}: ButtonsProps) => {
  const styles = useActionButtonStyles();
  const isTablet = useIsTablet();

  return (
    <div
      className={isTablet ? styles.tabletContainer : styles.compactContainer}>
      <SignatureButton
        row={data}
        status={status}
        refresh={refresh}
        approved={data.approved}
        iD_Course={iD_Course}
        iD_Instance={iD_Instance}
      />
      <ChoiceButton
        row={data}
        status={status}
        approved={data.approved}
        iD_Course={iD_Course}
        iD_Instance={iD_Instance}
      />
    </div>
  );
};

const CollabGrid = ({
  iD_Instance,
  iD_Course,
  status,
}: TabletCourseGridProps) => {
  const dispatch = useDispatch();
  const isTablet = useIsTablet();
  const loadCollabo = useLoadCollaborator(iD_Instance, iD_Course);

  React.useEffect(() => {
    dispatch({type: TABLETCOURSE, payload: {tablet: false, assistants: true}});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <IntegrityRoleGrid
        dataSource={loadCollabo}
        id={['employee_Nr']}
        allowIntegrity={false}
        allowActions={false}>
        <Column
          caption={i18n2._('Numero de empleado')}
          hidingPriority={4}
          dataField="employee_Nr"
        />
        <Column caption={i18n2._('Nombre')} dataField="name" width={200} />
        <Column caption={i18n2._('CWID')} dataField="cwid" />
        <Column caption={i18n2._('Aprobó')} dataField="approved" />
        <Column caption={i18n2._('Puntaje')} dataField="score" />
        <Column caption={i18n2._('Asistió')} dataField="present" />
        <Column
          caption={i18n2._('Fecha de actualizacion')}
          dataField="updateDate"
          defaultSortOrder={'desc'}
          dataType="date"
          format="dd/MM/yyyy"
          calculateCellValue={(row: any) => new Date(row.updateDate)}
        />
        <Column
          caption=""
          cellComponent={(e: any) => (
            <Buttons
              data={e.data.data}
              status={status}
              refresh={e.data.component.refresh}
              iD_Course={iD_Course}
              iD_Instance={iD_Instance}
            />
          )}
          width={getActionColumnWidth(2, isTablet)}
          fixed
          alignment="right"
        />
      </IntegrityRoleGrid>
    </>
  );
};

export default CollabGrid;
